// Agency display-currency conversion — PURE, and a LEAF (imports only types).
//
// ⚠️ LEAF ON PURPOSE, the googleAdsChartCatalog lesson again. BOTH dashboards'
// persistence modules import `NATIVE_DISPLAY_CURRENCY` / `normalizeDisplayCurrency`
// to decode the stored `ccy:` token, so this module must never reach into the
// component layer — a production bundle that happened to initialise a component
// first would leave a constant here `undefined` at the point a prefs module read
// it, which is the class of bug that white-screened the app once already.
//
// ── WHAT THIS IS FOR ───────────────────────────────────────────────────────
// The whole reporting pipeline carries money as `CurrencyAmount[]` — an amount
// PER NATIVE CURRENCY, deliberately never summed, because A$1 + NZ$1 = 2 of
// nothing. That rule is right and stays. What it cost the agency is a blended
// view: "how much did we spend across the whole portfolio" had no answer, only
// "A$60,176 · NZ$28,927".
//
// So: an agency-internal DISPLAY CURRENCY. Pick one and every money figure is
// converted through the monthly rate snapshot and summed. That is the deliberate,
// RATED version of the blend the per-currency design has been refusing — honest
// precisely because it goes through real rates and is labelled as converted.
//
// ⚠️⚠️ THE INVARIANT THAT MUST SURVIVE EVERY CHANGE HERE: a blended money figure
// is produced ONLY via real rates. In Native mode nothing blends (the caller
// keeps rendering per-currency); in display-currency mode every amount goes
// through `convertAmount`. A raw `amounts.reduce((n, a) => n + a.amount, 0)` must
// remain impossible in both modes — it is the one line that would turn this
// feature from "a converted total" into "a number that exists in no currency",
// while looking completely reasonable on screen. Mutation-checked.

import type { CurrencyAmount } from '../../types/reporting.js';
import type { FxRateSnapshot } from '../../types/fxRates.js';

/**
 * The default: no conversion, money renders per native currency exactly as it
 * always has. A sentinel rather than `null` so it can ride a preference token and
 * be compared without null-checks everywhere.
 */
export const NATIVE_DISPLAY_CURRENCY = 'native';

export function isNativeDisplay(value: string | null | undefined): boolean {
  return !value || value === NATIVE_DISPLAY_CURRENCY;
}

/** The currencies this snapshot can convert to, in stable order. */
export function displayCurrencyCodes(snapshot: FxRateSnapshot | null | undefined): string[] {
  if (!snapshot) return [];
  return Object.keys(snapshot.rates)
    .filter((code) => code !== NATIVE_DISPLAY_CURRENCY && isUsableRate(snapshot.rates[code]))
    .sort();
}

/**
 * Resolve a stored/selected display currency against the CURRENT snapshot.
 *
 * ⚠️ An unknown code falls back to Native, never to some other currency. A stored
 * "SGD" after SGD is dropped from the rate table must not silently start showing
 * AUD figures under an SGD label — showing native is the only answer that cannot
 * be wrong.
 */
export function normalizeDisplayCurrency(
  value: string | null | undefined,
  snapshot: FxRateSnapshot | null | undefined,
): string {
  if (isNativeDisplay(value)) return NATIVE_DISPLAY_CURRENCY;
  const code = String(value);
  return displayCurrencyCodes(snapshot).includes(code) ? code : NATIVE_DISPLAY_CURRENCY;
}

/** A rate is usable only if it is a finite, strictly positive number. */
function isUsableRate(rate: number | undefined): rate is number {
  return typeof rate === 'number' && Number.isFinite(rate) && rate > 0;
}

/**
 * Convert one amount between currencies through the snapshot's base.
 *
 * Returns `null` when either side has no usable rate — the caller then keeps that
 * amount NATIVE rather than guessing. A missing rate must never produce a number.
 */
export function convertAmount(
  amount: number,
  from: string,
  to: string,
  snapshot: FxRateSnapshot | null | undefined,
): number | null {
  if (!Number.isFinite(amount)) return null;
  if (from === to) return amount;
  if (!snapshot) return null;
  const fromRate = snapshot.rates[from];
  const toRate = snapshot.rates[to];
  if (!isUsableRate(fromRate) || !isUsableRate(toRate)) return null;
  // rates[X] = value of 1 BASE in X, so amount_X → base → Y is × toRate / fromRate.
  return (amount * toRate) / fromRate;
}

/** The result of blending a per-currency money stat into one display currency. */
export interface ConvertedMoney {
  /** The display currency the total is in. */
  currency: string;
  /** The converted, summed total — of the amounts we HAD a rate for. */
  amount: number;
  /**
   * Amounts we could NOT convert (no usable rate). ⚠️ They are excluded from
   * `amount` and handed back so the renderer can show them natively beside the
   * total. Folding them in unconverted would be exactly the raw blend this whole
   * module exists to prevent; dropping them silently would understate the total.
   */
  unconverted: CurrencyAmount[];
}

const r2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Blend a per-currency money stat into the display currency.
 *
 * Returns `null` when there is nothing to do — Native mode, no snapshot, or a
 * display currency the snapshot cannot reach. `null` means "render natively",
 * which is always a safe answer.
 */
export function toDisplayCurrency(
  amounts: readonly CurrencyAmount[] | undefined,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): ConvertedMoney | null {
  if (isNativeDisplay(display)) return null;
  if (!snapshot || !isUsableRate(snapshot.rates[display])) return null;

  let total = 0;
  const unconverted: CurrencyAmount[] = [];
  for (const entry of amounts ?? []) {
    const converted = convertAmount(entry.amount, entry.currency, display, snapshot);
    if (converted === null) unconverted.push(entry);
    else total += converted;
  }
  return { currency: display, amount: r2(total), unconverted };
}

/**
 * The scalar a chart plots for a money metric.
 *
 * ⚠️ In Native mode this returns the amount for `nativeCurrency` ONLY — a single
 * series can honestly draw one currency, and summing two would invent a figure.
 * In display-currency mode it returns the converted blend, which is a real
 * number in a real currency. Null when there is nothing measured either way, so
 * the bucket plots as a GAP rather than as a zero.
 */
export function moneySeriesValue(
  amounts: readonly CurrencyAmount[] | undefined,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
  nativeCurrency: string | null,
): number | null {
  const list = amounts ?? [];
  if (list.length === 0) return null;
  const converted = toDisplayCurrency(list, display, snapshot);
  if (converted) {
    // Everything unconvertible and nothing converted → we measured nothing IN
    // the display currency. A 0 would read as "they spent nothing".
    if (converted.unconverted.length === list.length) return null;
    return converted.amount;
  }
  const picked = nativeCurrency ? list.find((a) => a.currency === nativeCurrency) : list[0];
  return picked ? picked.amount : null;
}

/**
 * How a converted figure is labelled, so it can never be mistaken for a native
 * one. Null in Native mode — there is nothing to disclose.
 */
export function conversionNote(
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): string | null {
  if (isNativeDisplay(display) || !snapshot) return null;
  return `shown in ${display} · converted at ${snapshot.month} rates`;
}
